import { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormControl, FormHelperText, FormLabel } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FilePickerDialog from "./FilePickerDialog.jsx";
import DocumentViewer from "../../../MDDForm/Viewer/DocumentViewer.jsx";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";
import AttachmentFileRow from "./AttachmentFileRow.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useAttachmentConfig } from "./useAttachmentConfig.js";
import { useSnackbar } from "notistack";

/**
 * React Hook Form-connected file picker field.
 *
 * Integrates with RHF via the `control` + `name` props using <Controller>
 * internally — no need for the caller to wrap it themselves.
 *
 * The field value is:
 *   - `number | null`   when multiple={false}
 *   - `number[]`        when multiple={true}
 *
 * These are always real server IDs (Anlage.id). Files are uploaded
 * immediately when picked; on success the server ID is stored in RHF.
 * No additional resolve step is needed at submission time.
 *
 * Required props:
 *   name              — RHF field name
 *   existingFiles     — file[] fetched from the backend for this entity
 *   sessionFiles      — file[] from useSessionFiles (shared across fields)
 *   onAddSessionFile  — from useSessionFiles (async, awaits upload)
 *
 * Optional props:
 *   label             — field label
 *   helperText        — static helper text shown below the field
 *   rules             — RHF validation rules passed to <Controller>
 *   multiple          — boolean (default false)
 *   accept            — MIME/extension filter e.g. 'image/*,.pdf'
 *   dialogTitle       — override the dialog heading
 *   disabled          — boolean
 *   addDescription    — boolean (default false) — allow editing file descriptions
 *   onUpdateSessionFileDescription — callback to update session file description
 *   unselectableIds   — (string|number)[]  IDs that cannot be selected (e.g., already used in costs)
 *
 * Example (single file):
 *   <FilePickerField
 *     name="coverId"
 *     label="Cover image"
 *     accept="image/*"
 *     existingFiles={entityFiles}
 *     sessionFiles={sessionFiles}
 *     onAddSessionFile={addSessionFile}
 *     rules={{ required: 'Please select a cover image' }}
 *   />
 *
 * Example (multi-file):
 *   <FilePickerField
 *     name="attachmentIds"
 *     label="Attachments"
 *     multiple
 *     existingFiles={entityFiles}
 *     sessionFiles={sessionFiles}
 *     onAddSessionFile={addSessionFile}
 *   />
 */
export default function FormFilePickerField({
  // form
  name,
  rules,
  // display
  label,
  helperText,
  dialogTitle,
  // behaviour
  multiple = false,
  accept: acceptProp,
  disabled = false,
  addDescription = false,
  addUsedIn = false,
  usedInSuggestions = [],
  trackAsSource = false,
  showUsedInFilter = false,
  // file data
  existingFiles = [],
  sessionFiles = [],
  onAddSessionFile,
  onUpdateSessionFileDescription,
  onUpdateSessionFileUsedIn,
  // filtering
  unselectableIds = [],
  // form control - pass directly for FormRenderer integration, otherwise uses useFormContext
  control: controlProp,
}) {
  const configExtensions = useAttachmentConfig();
  const accept = acceptProp ?? configExtensions ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentToView, setDocumentToView] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (dialogOpen && addDescription && !onUpdateSessionFileDescription) {
      console.warn(
        "FormFilePickerField: onUpdateSessionFileDescription is undefined but addDescription=true"
      );
    }
  }, [dialogOpen, addDescription, onUpdateSessionFileDescription]);

  const allFiles = [...existingFiles, ...sessionFiles];

  const resolveFiles = (ids) =>
    ids.map((id) => allFiles.find((f) => f.id === id)).filter(Boolean);

  const handlePreview = async (file) => {
    const result = await backendGet(
      `data/anlages/${file.id}?include[]=filedata`,
      {
        label: "Datei laden",
        setIsLoading: () => {},
        setResult: () => {},
        enqueueSnackbar,
      }
    );

    if (result) {
      setDocumentToView(null);
      setTimeout(
        () =>
          setDocumentToView({
            success: true,
            id: result.id,
            filename: result.filename,
            filesize: result.filesize,
            filetype: result.filetype,
            mimetype: result.mimetype,
            filedata: result.filedata,
            label: result.bezeichnung || result.filename,
          }),
        0
      );
    } else {
      enqueueSnackbar("Fehler beim Laden der Datei", { variant: "error" });
    }
  };

  // Use provided control prop, or get from context (for external RHF usage)
  let control;
  try {
    const ctx = useFormContext();
    control = controlProp ?? ctx?.control;
  } catch (e) {
    // useFormContext throws when not inside FormProvider
    control = controlProp;
  }

  const renderContent = ({ field, fieldState }) => {
    // normalize value - handles {id, uuid} objects from field.value
    const rawValue = field.value ?? [];
    const selectedIds = multiple
      ? (Array.isArray(rawValue) ? rawValue : [rawValue])
          .map((ref) => ref?.id)
          .filter(Boolean)
      : rawValue?.id
        ? [rawValue.id]
        : [];

    const selectedFiles = resolveFiles(selectedIds);

    const buildAttachmentRefs = (files) => {
      const refs = files.map((f) => {
        const base = f.used_in ?? [];
        const tags =
          trackAsSource && label ? [...new Set([label, ...base])] : base;
        return { id: f.id, uuid: f.uuid ?? null, used_in: tags };
      });
      return multiple ? refs : (refs[0] ?? null);
    };

    const handleConfirm = (newIds) => {
      const files = resolveFiles(newIds);
      const refs = buildAttachmentRefs(files);
      field.onChange(refs);
      setDialogOpen(false);
    };

    const handleRemove = (id) => {
      const nextIds = selectedIds.filter((s) => s !== id);
      const remaining = resolveFiles(nextIds);
      const refs = buildAttachmentRefs(remaining);
      field.onChange(refs);
    };

    const hasError = Boolean(fieldState?.error);

    return (
      <FormControl error={hasError} fullWidth disabled={disabled}>
        {label && (
          <FormLabel
            sx={{
              mb: 1,
              typography: "body2",
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            {label}
          </FormLabel>
        )}

        <Box mb={2}>
          <Box mb={2}>
            {selectedFiles.map((f) => (
              <AttachmentFileRow
                key={f.id}
                file={f}
                usedIn={f.usedIn}
                onPreview={disabled ? null : () => handlePreview(f)}
                onRemove={disabled ? null : () => handleRemove(f.id)}
              />
            ))}
          </Box>

          {/* Trigger button */}
          <EditButton
            size="small"
            variant="outlined"
            color={hasError ? "error" : "primary"}
            startIcon={<AttachFileIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={disabled}
            onBlur={field.onBlur}
            sx={{ mt: selectedFiles.length > 0 ? 1 : 0 }}
          >
            {selectedFiles.length > 0
              ? multiple
                ? "Auswahl bearbeiten"
                : "Bearbeiten"
              : multiple
                ? "Dateien auswählen"
                : "Datei auswählen"}
          </EditButton>
        </Box>

        {/* Validation error takes priority over static helper text */}
        {(hasError || helperText) && (
          <FormHelperText>
            {hasError ? fieldState.error.message : helperText}
          </FormHelperText>
        )}

        <FilePickerDialog
          open={dialogOpen}
          onClose={() => {
            field.onBlur(); // mark touched on cancel too
            setDialogOpen(false);
          }}
          onConfirm={handleConfirm}
          existingFiles={existingFiles}
          sessionFiles={sessionFiles}
          onAddSessionFile={onAddSessionFile}
          onUpdateSessionFileDescription={onUpdateSessionFileDescription}
          onUpdateSessionFileUsedIn={onUpdateSessionFileUsedIn}
          initialSelectedIds={selectedIds}
          multiple={multiple}
          accept={accept}
          title={dialogTitle}
          addDescription={addDescription}
          addUsedIn={addUsedIn}
          usedInSuggestions={usedInSuggestions}
          showUsedInFilter={showUsedInFilter}
          unselectableIds={unselectableIds}
        />

        <DocumentViewer key={documentToView?.id} document={documentToView} />
      </FormControl>
    );
  };

  // with RHF control, use Controller, otherwise render directly with passed value/onChange
  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={renderContent}
      />
    );
  }

  // fallback for non-RHF usage for FormRenderer
  return renderContent({
    field: { value: [], onChange: () => {}, onBlur: () => {} },
    fieldState: { error: null },
  });
}
